
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Award, 
  Shield, 
  Download, 
  Mail,
  FileText,
  CheckCircle
} from 'lucide-react';
import { useStaffCapabilities } from '@/hooks/auth/useStaffCapabilities';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';

interface Certificate {
  id: string;
  user_id: string;
  user_name: string;
  course_id: string;
  course_title: string;
  certificate_number: string;
  issue_date: string;
  verification_hash: string;
  is_verified: boolean;
  metadata: any;
}

interface StaffCertificateActionsProps {
  certificates: Certificate[];
}

const StaffCertificateActions: React.FC<StaffCertificateActionsProps> = ({ certificates }) => {
  const { verifyCertificate, downloadCertificate, loading } = useStaffCapabilities();
  const [selectedCertificates, setSelectedCertificates] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSelectAll = () => {
    if (selectedCertificates.length === certificates.length) {
      setSelectedCertificates([]);
    } else {
      setSelectedCertificates(certificates.map(cert => cert.id));
    }
  };

  const handleSelectCertificate = (certificateId: string) => {
    setSelectedCertificates(prev => 
      prev.includes(certificateId)
        ? prev.filter(id => id !== certificateId)
        : [...prev, certificateId]
    );
  };

  const handleBulkVerify = async () => {
    if (selectedCertificates.length === 0) {
      toast.error('Please select certificates to verify');
      return;
    }

    setActionLoading(true);
    try {
      let successCount = 0;
      for (const certId of selectedCertificates) {
        const success = await verifyCertificate(certId);
        if (success) successCount++;
      }
      
      toast.success(`Verified ${successCount} out of ${selectedCertificates.length} certificates`);
      setSelectedCertificates([]);
    } catch (error) {
      console.error('Error in bulk verify:', error);
      toast.error('Failed to verify certificates');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedCertificates.length === 0) {
      toast.error('Please select certificates to download');
      return;
    }

    setActionLoading(true);
    try {
      let successCount = 0;
      for (const certId of selectedCertificates) {
        const success = await downloadCertificate(certId);
        if (success) successCount++;
      }
      
      toast.success(`Initiated download for ${successCount} certificates`);
      setSelectedCertificates([]);
    } catch (error) {
      console.error('Error in bulk download:', error);
      toast.error('Failed to download certificates');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendEmails = async () => {
    if (selectedCertificates.length === 0) {
      toast.error('Please select certificates to send emails for');
      return;
    }

    setActionLoading(true);
    try {
      // Get certificate details for selected certificates
      const selectedCerts = certificates.filter(cert => selectedCertificates.includes(cert.id));
      
      let successCount = 0;
      for (const cert of selectedCerts) {
        try {
          // Call the email edge function for each certificate
          const { data, error } = await supabase.functions.invoke('send-certificate-email', {
            body: {
              certificateId: cert.id,
              userEmail: cert.user_name, // This should be actual email in real data
              userName: cert.user_name,
              courseTitle: cert.course_title,
              certificateNumber: cert.certificate_number
            }
          });

          if (error) throw error;
          successCount++;
        } catch (error) {
          console.error(`Failed to send email for certificate ${cert.id}:`, error);
        }
      }

      toast.success(`Email notifications sent for ${successCount} out of ${selectedCertificates.length} certificates`);
      setSelectedCertificates([]);
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send email notifications');
    } finally {
      setActionLoading(false);
    }
  };

  const unverifiedCertificates = certificates.filter(cert => !cert.is_verified);
  const verifiedCertificates = certificates.filter(cert => cert.is_verified);

  return (
    <div className="space-y-6">
      {/* Bulk Actions Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Bulk Certificate Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedCertificates.length === certificates.length && certificates.length > 0}
                onCheckedChange={handleSelectAll}
                disabled={certificates.length === 0}
              />
              <span className="text-sm font-medium">
                Select All ({selectedCertificates.length} of {certificates.length} selected)
              </span>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleBulkVerify}
                disabled={selectedCertificates.length === 0 || actionLoading}
                size="sm"
                variant="outline"
              >
                <Shield className="h-4 w-4 mr-2" />
                Bulk Verify
              </Button>
              
              <Button
                onClick={handleBulkDownload}
                disabled={selectedCertificates.length === 0 || actionLoading}
                size="sm"
                variant="outline"
              >
                <Download className="h-4 w-4 mr-2" />
                Bulk Download
              </Button>
              
              <Button
                onClick={handleSendEmails}
                disabled={selectedCertificates.length === 0 || actionLoading}
                size="sm"
                variant="outline"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Emails
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Action Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Certificates</p>
                <p className="text-2xl font-bold">{certificates.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Verified</p>
                <p className="text-2xl font-bold text-green-600">{verifiedCertificates.length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Verification</p>
                <p className="text-2xl font-bold text-orange-600">{unverifiedCertificates.length}</p>
              </div>
              <Shield className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificate Selection List */}
      <Card>
        <CardHeader>
          <CardTitle>Certificate Selection</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {certificates.map(certificate => (
              <div key={certificate.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedCertificates.includes(certificate.id)}
                    onCheckedChange={() => handleSelectCertificate(certificate.id)}
                  />
                  <div>
                    <p className="font-medium">{certificate.user_name}</p>
                    <p className="text-sm text-muted-foreground">{certificate.course_title}</p>
                    <p className="text-xs text-muted-foreground">{certificate.certificate_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={certificate.is_verified ? "default" : "secondary"}>
                    {certificate.is_verified ? "Verified" : "Pending"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(certificate.issue_date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {certificates.length === 0 && (
            <div className="text-center py-8">
              <Award className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No certificates available for bulk actions</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions for Unverified Certificates */}
      {unverifiedCertificates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-orange-600">Quick Actions for Unverified Certificates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <Button
                onClick={() => {
                  setSelectedCertificates(unverifiedCertificates.map(cert => cert.id));
                  handleBulkVerify();
                }}
                disabled={actionLoading}
                className="flex-1"
              >
                <Shield className="h-4 w-4 mr-2" />
                Verify All Unverified ({unverifiedCertificates.length})
              </Button>
              
              <Button
                onClick={() => setSelectedCertificates(unverifiedCertificates.map(cert => cert.id))}
                variant="outline"
                className="flex-1"
              >
                Select All Unverified
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffCertificateActions;
